/**
 * SVG `<text>` → editor text-layer descriptor.
 *
 * Tier 2 Group F SVG round-trip polish (PR-2) — closes the deferred
 * "native text layer mapping" item from the SVG import plan.
 *
 * Scope (v1):
 *
 *  - Single-line `<text>` elements with their text content as a
 *    direct child text node (no `<tspan>` children, no `<textPath>`
 *    children). Multi-line + on-path text continues to rasterize
 *    via the existing fallback — those models need editor surgery
 *    (TextSettings has no multi-line span model) and are deferred.
 *
 *  - Attributes parsed: `x`, `y` (positioning), `font-family`,
 *    `font-size`, `font-weight`, `font-style`, `fill`,
 *    `text-anchor` (alignment), `letter-spacing`,
 *    `text-decoration` (underline). Both attribute and inline-style
 *    sources honored (style wins per CSS cascade order).
 *
 *  - SVG `<text>` baselines text on `y` (font baseline, NOT bbox
 *    top). The editor's text layers use a top-left bbox origin.
 *    We approximate the conversion by subtracting `font-size * 0.8`
 *    from `y` (a typical baseline-to-top ratio). The browser-side
 *    IText materialization then renders at the corrected position;
 *    the inverse correction happens on export.
 *
 * Returns `null` when the element has features the editor's text
 * model can't represent — caller routes those to the existing
 * raster fallback.
 *
 * Pure module — no DOM dependencies beyond the standard `Element`
 * API. Tests inject `XMLDocument` produced by `DOMParser`.
 */

import type { TextSettings, TextStrokeSettings, TextShadowSettings } from '../types';
import { BASELINE_TO_TOP_RATIO } from './text-baseline-constants';

/** Reasons a `<text>` element can't be expressed as a native
 *  editor text layer. Surfaced via the existing `SvgImportWarning`
 *  reason `'text'` (the caller doesn't need to discriminate
 *  between "no native model" sub-cases). */
export type TextImportRejectReason =
  | 'has-tspan-style-variance'
  | 'has-text-path-and-tspan'
  | 'text-path-ref-unresolvable'
  | 'has-text-area'
  | 'on-path'
  | 'gradient-or-pattern-fill'
  | 'gradient-or-pattern-stroke'
  | 'empty-text'
  | 'transform-non-trivial';

/** A successfully parsed text element, ready for IText
 *  materialization in the canvas hook. Position is in scene
 *  coordinates (the walker's transform-baking pass has already
 *  applied the parent transform). */
export interface ParsedSvgTextLayer {
  /** The text string. Can contain `\n` for multi-line (via
   *  literal newlines or joined `<tspan>` children). */
  text: string;
  /** Scene-space top-left of the text bbox. SVG's `(x, y)` is on
   *  the baseline; we offset to top-left here so the editor's
   *  bbox-origin model lines up. */
  x: number;
  y: number;
  /** Full editor TextSettings shape — every required field
   *  populated (defaults filled from SVG defaults / system
   *  conventions when the attribute is absent). */
  settings: TextSettings;
  /** Editor LayerNode.id of the vector layer this text is bound to
   *  (text-on-path via `<textPath>`). Set only when the SVG used a
   *  `<textPath href="#pathId">` whose target resolved to a vector
   *  layer that the walker imported. Caller applies the binding
   *  via `LayerNode.textPath = { pathLayerId }`. */
  textPathLayerId?: string;
}

export interface TextImportFailure {
  reason: TextImportRejectReason;
}

export type TextImportResult =
  | { ok: true; value: ParsedSvgTextLayer }
  | { ok: false; failure: TextImportFailure };

/** Default font size when `<text>` doesn't specify one. SVG's
 *  initial `font-size` is `medium`, which browsers render as 16px.
 *  Matches the editor's default for new text. */
const DEFAULT_FONT_SIZE = 16;

/** Default font family. SVG initial value is `serif`; the editor's
 *  text tool defaults to `Arial`. Pick `serif` so imported text
 *  without an explicit font matches the original SVG's rendering;
 *  the user can change it post-import. */
const DEFAULT_FONT_FAMILY = 'serif';

// BASELINE_TO_TOP_RATIO is imported from `text-baseline-constants.ts`
// — the single source of truth shared with svg-export, psd-text-
// import, and psd-text-export so round-trip stays bit-stable.

/** Parse a `<text>` element + (already-composed) effective
 *  transform into a native text layer descriptor. The caller
 *  (`svg-import.ts` walker) routes the result to either a
 *  text-type LayerNode (success) or the existing raster fallback
 *  (failure).
 *
 *  Accepts the same `GradientDefs` map the gradient resolver
 *  uses so a `fill="url(#g1)"` ref can be rejected here too
 *  — text gradient fills require the editor's TextSettings.fill
 *  to model gradient state, which it doesn't. */
/** Optional walker state passed by `svg-import.ts` so the parser
 *  can resolve `<textPath href="#pathId">` to the editor's vector
 *  layer that imported that path. The map is populated during the
 *  pre-order walk; text elements that appear AFTER their bound
 *  path resolve naturally. Forward refs (text before path) won't
 *  resolve and fall back to raster. */
export interface SvgTextParserContext {
  /** Map of SVG element `id` → editor `LayerNode.id` for path-
   *  emitting elements (path / rect / circle / ellipse / line /
   *  polyline / polygon) that the walker has already imported. */
  svgIdToLayerId: ReadonlyMap<string, string>;
}

export function parseSvgTextElement(
  el: Element,
  effectiveTransform: readonly [number, number, number, number, number, number],
  ctx?: SvgTextParserContext,
): TextImportResult {
  // Reject `<textArea>` (SVG 1.2) children — flow-area rendering
  // doesn't have a direct editor model analog.
  const childTags = Array.from(el.children).map((c) => c.localName.toLowerCase());
  if (childTags.includes('textarea')) return reject('has-text-area');

  // `<textPath>` — text bound to another path's geometry. Editor
  // models this via `LayerNode.textPath = { pathLayerId }`; the
  // bound path must be an already-imported vector layer.
  // Mixing `<textPath>` with sibling `<tspan>` isn't supported
  // (would require per-segment styling).
  if (childTags.includes('textpath')) {
    if (childTags.includes('tspan')) return reject('has-text-path-and-tspan');
    return parseTextWithTextPath(el, effectiveTransform, ctx);
  }

  // text-on-path via element attribute (rare; <text path="..."> is
  // SVG 1.2 only). Reject so the rasterizer handles it.
  if (el.hasAttribute('path')) return reject('on-path');

  // Multi-line via `<tspan>` — accepted when each tspan only adds
  // a line break (no per-tspan style variance). Tspans with
  // style-affecting attributes (font-family, font-size, fill,
  // stroke, etc.) are rejected so the rasterizer captures the
  // mixed styling.
  //
  // text-content resolution:
  //   - When the `<text>` has direct text node children only,
  //     `textContent` is the literal payload.
  //   - When `<text>` has `<tspan>` children, walk them and
  //     concatenate with `\n` so the result is a multi-line
  //     string (Fabric IText handles `\n` natively via
  //     `lineHeight`).
  const xmlSpace = el.getAttribute('xml:space');
  let text: string;
  if (childTags.includes('tspan')) {
    const tspanResult = readUniformStyleTspans(el, xmlSpace === 'preserve');
    if (!tspanResult.ok) return reject(tspanResult.reason);
    text = tspanResult.text;
  } else {
    const rawText = el.textContent ?? '';
    if (!rawText.trim()) return reject('empty-text');
    // SVG's `xml:space` attribute controls whitespace handling.
    // Default collapses runs of whitespace; `'preserve'` keeps
    // them verbatim. (Pre-PR review #288 R1 Major.)
    text = xmlSpace === 'preserve' ? rawText : rawText.trim();
  }
  if (!text) return reject('empty-text');

  // Non-trivial transform (rotation / skew baked into matrix)
  // can't be expressed on a text layer cleanly — IText supports
  // an angle, but a non-uniform-scale matrix can't roundtrip.
  // Accept rotation-only transforms; reject anything with a skew
  // component or unequal axes. The editor's text layer model
  // can be extended later (Tier 3) to carry a full affine matrix.
  if (hasNonRotationComponent(effectiveTransform)) return reject('transform-non-trivial');

  const styleAttr = el.getAttribute('style') ?? '';

  // Fill — solid color or undefined (omitted). Gradient/pattern
  // refs reject (text gradient fills aren't in TextSettings).
  const fillResult = parseTextFill(el, styleAttr);
  if (fillResult.kind === 'reject') return reject(fillResult.reason);

  // Stroke — optional; mirrors the vector path stroke model.
  // Gradient/pattern stroke refs reject for the same reason.
  const strokeResult = parseTextStroke(el, styleAttr);
  if (strokeResult.kind === 'reject') return reject(strokeResult.reason);

  const fontFamily =
    readStyleProp(styleAttr, 'font-family') ??
    el.getAttribute('font-family') ??
    DEFAULT_FONT_FAMILY;
  const fontSize = parseLengthOrFallback(
    readStyleProp(styleAttr, 'font-size') ?? el.getAttribute('font-size'),
    DEFAULT_FONT_SIZE,
  );
  const fontWeight: TextSettings['fontWeight'] = isBoldWeight(
    readStyleProp(styleAttr, 'font-weight') ?? el.getAttribute('font-weight'),
  )
    ? 'bold'
    : 'normal';
  const fontStyle: TextSettings['fontStyle'] =
    (readStyleProp(styleAttr, 'font-style') ?? el.getAttribute('font-style')) === 'italic'
      ? 'italic'
      : 'normal';
  const textDecoration =
    readStyleProp(styleAttr, 'text-decoration') ?? el.getAttribute('text-decoration') ?? '';
  const underline = /underline/i.test(textDecoration);

  // text-anchor: start | middle | end → editor: left | center | right
  const anchor = readStyleProp(styleAttr, 'text-anchor') ?? el.getAttribute('text-anchor');
  const textAlign: TextSettings['textAlign'] =
    anchor === 'middle' ? 'center' : anchor === 'end' ? 'right' : 'left';

  // letter-spacing in SVG is a length (px); editor stores it in
  // thousandths of em (Fabric's IText charSpacing convention).
  const letterSpacingRaw =
    readStyleProp(styleAttr, 'letter-spacing') ?? el.getAttribute('letter-spacing') ?? '0';
  const letterSpacingPx = parseLengthOrFallback(letterSpacingRaw, 0);
  // px → em → em × 1000
  const letterSpacing = fontSize > 0 ? Math.round((letterSpacingPx / fontSize) * 1000) : 0;

  // line-height — editor stores as a unitless multiplier. SVG
  // doesn't have a direct equivalent on `<text>` (the spec uses
  // `dy` between successive lines, which only applies to multi-
  // line text we're rejecting). Default to 1.16 (Fabric's IText
  // default).
  const lineHeight = parseLengthOrFallback(
    readStyleProp(styleAttr, 'line-height') ?? el.getAttribute('line-height'),
    1.16,
  );

  // Position — SVG's (x, y) is on the baseline.
  const xAttr = parseLengthOrFallback(el.getAttribute('x'), 0);
  const yAttrBaseline = parseLengthOrFallback(el.getAttribute('y'), 0);
  // Compose with the effective transform: matrix maps source coords
  // (x, baseline-y) into scene coords.
  const baselineScene = applyMatrixToPoint(effectiveTransform, xAttr, yAttrBaseline);

  // Anchor-adjust: x is the baseline for the START of glyphs at
  // text-anchor=start. For middle/end, x is the horizontal CENTER
  // / END. The editor's text layer uses top-LEFT as its origin
  // attribute regardless of textAlign — Fabric handles the
  // anchor visually based on the textAlign property. So we don't
  // shift x; we just record the SVG-x as-is.

  // Y conversion: baseline → top.
  const topY = baselineScene.y - fontSize * BASELINE_TO_TOP_RATIO;

  const settings = buildSettingsCore(
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    underline,
    textAlign,
    fillResult.color,
    letterSpacing,
    lineHeight,
    strokeResult.stroke,
    parseDropShadowFromStyle(styleAttr),
  );

  return {
    ok: true,
    value: {
      text,
      x: baselineScene.x,
      y: topY,
      settings,
    },
  };
}

/** Compose a `TextSettings` from parsed primitives. Shared between
 *  the plain-text path and the textPath / tspan paths so styling
 *  defaults (arch=0 etc.) stay consistent. */
function buildSettingsCore(
  fontFamily: string,
  fontSize: number,
  fontWeight: TextSettings['fontWeight'],
  fontStyle: TextSettings['fontStyle'],
  underline: boolean,
  textAlign: TextSettings['textAlign'],
  fill: string,
  letterSpacing: number,
  lineHeight: number,
  stroke: TextStrokeSettings | undefined,
  dropShadow: TextShadowSettings | undefined,
): TextSettings {
  return {
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    underline,
    textAlign,
    fill,
    arch: 0,
    letterSpacing,
    lineHeight,
    ...(stroke ? { stroke } : {}),
    ...(dropShadow ? { dropShadow } : {}),
  };
}

/** Build a `TextSettings` from an element + style attr — used by
 *  the textPath path which needs the same attribute resolution
 *  as the main parser. */
function buildTextSettings(
  el: Element,
  styleAttr: string,
  fillResult: { kind: 'ok'; color: string } | { kind: 'reject'; reason: TextImportRejectReason },
  strokeResult:
    | { kind: 'ok'; stroke: TextStrokeSettings | undefined }
    | { kind: 'reject'; reason: TextImportRejectReason },
): TextSettings {
  if (fillResult.kind === 'reject' || strokeResult.kind === 'reject') {
    // Caller already rejected — but TS narrowing requires us to
    // produce a TextSettings. This branch is unreachable in
    // practice.
    throw new Error('buildTextSettings called with rejection results');
  }
  const fontFamily =
    readStyleProp(styleAttr, 'font-family') ??
    el.getAttribute('font-family') ??
    DEFAULT_FONT_FAMILY;
  const fontSize = parseLengthOrFallback(
    readStyleProp(styleAttr, 'font-size') ?? el.getAttribute('font-size'),
    DEFAULT_FONT_SIZE,
  );
  const fontWeight: TextSettings['fontWeight'] = isBoldWeight(
    readStyleProp(styleAttr, 'font-weight') ?? el.getAttribute('font-weight'),
  )
    ? 'bold'
    : 'normal';
  const fontStyle: TextSettings['fontStyle'] =
    (readStyleProp(styleAttr, 'font-style') ?? el.getAttribute('font-style')) === 'italic'
      ? 'italic'
      : 'normal';
  const textDecoration =
    readStyleProp(styleAttr, 'text-decoration') ?? el.getAttribute('text-decoration') ?? '';
  const underline = /underline/i.test(textDecoration);
  const anchor = readStyleProp(styleAttr, 'text-anchor') ?? el.getAttribute('text-anchor');
  const textAlign: TextSettings['textAlign'] =
    anchor === 'middle' ? 'center' : anchor === 'end' ? 'right' : 'left';
  const letterSpacingRaw =
    readStyleProp(styleAttr, 'letter-spacing') ?? el.getAttribute('letter-spacing') ?? '0';
  const letterSpacingPx = parseLengthOrFallback(letterSpacingRaw, 0);
  const letterSpacing = fontSize > 0 ? Math.round((letterSpacingPx / fontSize) * 1000) : 0;
  const lineHeight = parseLengthOrFallback(
    readStyleProp(styleAttr, 'line-height') ?? el.getAttribute('line-height'),
    1.16,
  );
  return buildSettingsCore(
    fontFamily,
    fontSize,
    fontWeight,
    fontStyle,
    underline,
    textAlign,
    fillResult.color,
    letterSpacing,
    lineHeight,
    strokeResult.stroke,
    parseDropShadowFromStyle(styleAttr),
  );
}

function reject(reason: TextImportRejectReason): TextImportResult {
  return { ok: false, failure: { reason } };
}

/** Attribute names on `<tspan>` that would imply per-line style
 *  variance the editor's TextSettings can't carry. Presence of any
 *  of these on a tspan child rejects the whole `<text>` to raster,
 *  so the rasterizer captures the original mixed styling. Position
 *  attrs (`x` / `y` / `dx` / `dy`) are NOT in this set — they
 *  represent line break positioning, which we accept as "line
 *  break + uniform styling carried over from `<text>`". */
const TSPAN_STYLE_VARYING_ATTRS = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant',
  'font-stretch',
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'text-decoration',
  'letter-spacing',
  'word-spacing',
  'text-anchor',
  'dominant-baseline',
  'baseline-shift',
  'direction',
  'unicode-bidi',
  'opacity',
  'style',
] as const;

/** Handle `<text><textPath href="#pathId">...</textPath></text>`.
 *  Resolves the href against the walker-provided
 *  `svgIdToLayerId` map. When the path is unresolvable (id not
 *  found, or context not passed in), the import falls back to
 *  raster via the caller's rejection path. */
function parseTextWithTextPath(
  textEl: Element,
  effectiveTransform: readonly [number, number, number, number, number, number],
  ctx: SvgTextParserContext | undefined,
): TextImportResult {
  // Find the textPath DIRECT child + extract its href ref.
  // SVG spec requires `<textPath>` to be a direct child of
  // `<text>` (section 10.5); a nested form like
  // `<text><g><textPath/></g></text>` isn't valid. Using
  // `getElementsByTagName` would match descendants at any depth,
  // accepting malformed structures we shouldn't. (Pre-PR review
  // R1 Critical.)
  const textPathEl = Array.from(textEl.children).find(
    (child) => child.localName.toLowerCase() === 'textpath',
  );
  if (!textPathEl) return reject('text-path-ref-unresolvable');
  const hrefRaw = textPathEl.getAttribute('href') ?? textPathEl.getAttribute('xlink:href') ?? null;
  if (!hrefRaw) return reject('text-path-ref-unresolvable');
  const fragmentRefId = hrefRaw.startsWith('#') ? hrefRaw.slice(1) : null;
  if (!fragmentRefId) return reject('text-path-ref-unresolvable');
  if (!ctx?.svgIdToLayerId) return reject('text-path-ref-unresolvable');
  const targetLayerId = ctx.svgIdToLayerId.get(fragmentRefId);
  if (!targetLayerId) return reject('text-path-ref-unresolvable');

  // Recurse the basic text-element parsing for everything BUT the
  // text content + the textPath. We need the `<text>`'s own
  // attributes (font-family, fill, etc.) — same as a plain
  // `<text>` — but the content comes from the textPath body.
  //
  // Easier: clone the textEl, replace the textPath child with its
  // text content, then run the regular parse. But we want to keep
  // the original element untouched for the stash. Instead, take a
  // shortcut: reuse the same attribute-reading helpers below.
  const rawText = textPathEl.textContent ?? '';
  if (!rawText.trim()) return reject('empty-text');
  const xmlSpace = textEl.getAttribute('xml:space') ?? textPathEl.getAttribute('xml:space');
  const text = xmlSpace === 'preserve' ? rawText : rawText.trim();
  if (hasNonRotationComponent(effectiveTransform)) return reject('transform-non-trivial');

  const styleAttr = textEl.getAttribute('style') ?? '';
  const fillResult = parseTextFill(textEl, styleAttr);
  if (fillResult.kind === 'reject') return reject(fillResult.reason);
  const strokeResult = parseTextStroke(textEl, styleAttr);
  if (strokeResult.kind === 'reject') return reject(strokeResult.reason);

  const settings = buildTextSettings(textEl, styleAttr, fillResult, strokeResult);

  // textPath text doesn't have its own (x, y) — the path defines
  // the layout. Use (0, 0) in scene coords; Fabric IText's path
  // binding handles the rendering position automatically.
  return {
    ok: true,
    value: {
      text,
      x: 0,
      y: 0,
      settings,
      textPathLayerId: targetLayerId,
    },
  };
}

/** Walk the `<text>` element's `<tspan>` children and concatenate
 *  their text content into a single newline-joined string. Rejects
 *  when any tspan carries a style-varying attribute — the resulting
 *  text needs uniform `TextSettings` to render natively. */
function readUniformStyleTspans(
  textEl: Element,
  preserveWhitespace: boolean,
): { ok: true; text: string } | { ok: false; reason: TextImportRejectReason } {
  // Walk children in document order, handling both direct text
  // nodes (rare but valid — `<text>Foo<tspan>Bar</tspan></text>`
  // is legal SVG) and `<tspan>` element children.
  const parts: string[] = [];
  let sawTspan = false;
  for (const node of Array.from(textEl.childNodes)) {
    if (node.nodeType === 3 /* Node.TEXT_NODE */) {
      const raw = node.textContent ?? '';
      const trimmed = preserveWhitespace ? raw : raw.trim();
      if (trimmed) parts.push(trimmed);
      continue;
    }
    if (node.nodeType !== 1 /* Node.ELEMENT_NODE */) continue;
    const child = node as Element;
    if (child.localName.toLowerCase() !== 'tspan') continue;
    sawTspan = true;
    // Reject if the tspan has any style-varying attribute. Nested
    // `<tspan><tspan>` would also trip this (the outer tspan's
    // children we walk below would still be tspans) — but for
    // simplicity we reject any nesting beyond one level by checking
    // for grandchild tspans too.
    if (child.getElementsByTagName('tspan').length > 0) {
      return { ok: false, reason: 'has-tspan-style-variance' };
    }
    for (const attr of TSPAN_STYLE_VARYING_ATTRS) {
      if (child.hasAttribute(attr)) {
        return { ok: false, reason: 'has-tspan-style-variance' };
      }
    }
    const raw = child.textContent ?? '';
    const text = preserveWhitespace ? raw : raw.trim();
    if (text) parts.push(text);
  }
  if (!sawTspan) {
    // Caller invoked us under the assumption tspans existed; the
    // tag-list scan must have been a false positive. Treat as no
    // tspans found and let the caller's regular text path run.
    return { ok: true, text: textEl.textContent ?? '' };
  }
  if (parts.length === 0) {
    return { ok: false, reason: 'empty-text' };
  }
  // Multi-line: join with newlines. Fabric IText splits on `\n`
  // and renders each segment as a separate line using the layer's
  // `lineHeight` multiplier.
  return { ok: true, text: parts.join('\n') };
}

type FillParseResult =
  | { kind: 'ok'; color: string }
  | { kind: 'reject'; reason: TextImportRejectReason };

function parseTextFill(el: Element, styleAttr: string): FillParseResult {
  const raw = readStyleProp(styleAttr, 'fill') ?? el.getAttribute('fill');
  if (raw === null) return { kind: 'ok', color: '#000000' }; // SVG default
  if (raw === 'none') return { kind: 'ok', color: 'transparent' };
  if (/^url\s*\(/i.test(raw)) {
    // Gradient/pattern fill — editor's TextSettings.fill is a
    // string; can't model gradient stops on text in v1. Reject
    // so the rasterizer renders the original gradient-filled
    // glyphs.
    return { kind: 'reject', reason: 'gradient-or-pattern-fill' };
  }
  return { kind: 'ok', color: raw };
}

type StrokeParseResult =
  | { kind: 'ok'; stroke: TextStrokeSettings | undefined }
  | { kind: 'reject'; reason: TextImportRejectReason };

function parseTextStroke(el: Element, styleAttr: string): StrokeParseResult {
  const raw = readStyleProp(styleAttr, 'stroke') ?? el.getAttribute('stroke');
  if (raw === null || raw === 'none') return { kind: 'ok', stroke: undefined };
  if (/^url\s*\(/i.test(raw)) {
    return { kind: 'reject', reason: 'gradient-or-pattern-stroke' };
  }
  const widthRaw =
    readStyleProp(styleAttr, 'stroke-width') ?? el.getAttribute('stroke-width') ?? '1';
  const width = parseLengthOrFallback(widthRaw, 1);
  // Position: SVG `<text>` strokes are always centered on the
  // glyph path. The editor's stroke model has 'outside' / 'center'
  // / 'inside' choices; 'center' is the faithful import value.
  return {
    kind: 'ok',
    stroke: { color: raw, width, position: 'center' },
  };
}

/** Parse `filter: drop-shadow(x y blur color)` from a style
 *  attribute, returning the editor's `TextShadowSettings`.
 *  Returns undefined when the style doesn't contain a drop-shadow
 *  filter. We don't parse SVG `<filter>` element refs — those
 *  rasterize via the existing presentation-attrs path.
 *
 *  Color handling: the CSS spec allows hex / named / `rgb(...)` /
 *  `rgba(...)` etc. — the multi-token forms (`rgb(255, 0, 0)`)
 *  would naively split into garbage. We pull the trailing color
 *  via a balanced-paren grab from the source string instead of
 *  splitting on whitespace. (Pre-PR review Critical #2.) */
function parseDropShadowFromStyle(styleAttr: string): TextShadowSettings | undefined {
  const filterValue = readStyleProp(styleAttr, 'filter');
  if (!filterValue) return undefined;
  // Match `drop-shadow(...)` and capture the inner argument list,
  // allowing the inner content to contain balanced parens (one
  // level deep — enough for nested `rgb(...)` / `hsla(...)` calls
  // but not for arbitrary CSS color functions like
  // `color-mix(in srgb, ...)`).
  const match = /drop-shadow\s*\(\s*((?:[^()]|\([^()]*\))+)\s*\)/i.exec(filterValue);
  if (!match) return undefined;
  // Split the inner content into the three lengths + the color
  // tail. The color may itself contain commas + spaces inside a
  // `rgb(...)` / `hsl(...)` call, so we scan for the first paren-
  // balanced color token after the three lengths.
  const inner = match[1].trim();
  // Greedy capture: three whitespace-separated tokens for the
  // lengths, then anything else is the color. Tokens may contain
  // CSS units (`2px` / `1.5em`) but the color is either a hex,
  // named color, or paren-balanced function call.
  const lengthMatch = /^(\S+)\s+(\S+)\s+(\S+)\s+(.+)$/.exec(inner);
  if (!lengthMatch) return undefined;
  const offsetX = parseLengthOrFallback(lengthMatch[1], 0);
  const offsetY = parseLengthOrFallback(lengthMatch[2], 0);
  const blur = parseLengthOrFallback(lengthMatch[3], 0);
  const color = lengthMatch[4].trim() || '#000000';
  return { offsetX, offsetY, blur, color };
}

/** Read a key from a `style` attribute string. Tolerates the
 *  conventional `key: value;` separator and skips quoted strings.
 *  Conservative parse — not a full CSS tokenizer. */
function readStyleProp(styleAttr: string, prop: string): string | null {
  if (!styleAttr) return null;
  const lower = prop.toLowerCase();
  for (const part of styleAttr.split(';')) {
    const colonIdx = part.indexOf(':');
    if (colonIdx < 0) continue;
    const key = part.slice(0, colonIdx).trim().toLowerCase();
    if (key === lower) return part.slice(colonIdx + 1).trim();
  }
  return null;
}

/** Parse a length value (`12px`, `1.5em`, `12`, `12pt`). Strips
 *  the unit and returns the bare number. Unit conversion is
 *  approximate — we don't have access to the parent context's
 *  font-size to resolve em, so we treat em as 16x (typical
 *  default).
 *
 *  `null` / `undefined` / empty / unparseable input returns
 *  `fallback`. */
function parseLengthOrFallback(raw: string | null | undefined, fallback: number): number {
  if (!raw) return fallback;
  const trimmed = raw.trim();
  if (!trimmed) return fallback;
  // Match a number followed by an optional unit.
  const match = /^(-?\d*\.?\d+)([a-z%]*)$/i.exec(trimmed);
  if (!match) return fallback;
  const n = parseFloat(match[1]);
  if (Number.isNaN(n)) return fallback;
  const unit = (match[2] || 'px').toLowerCase();
  switch (unit) {
    case 'px':
    case '':
      return n;
    case 'pt':
      return n * (96 / 72); // 1pt = 1.333px
    case 'em':
    case 'rem':
      return n * 16; // approximate
    case 'pc':
      return n * 16; // 1pc = 12pt = 16px
    case '%':
      return n; // treat as bare number — caller decides
    default:
      return n;
  }
}

function isBoldWeight(raw: string | null): boolean {
  if (!raw) return false;
  const trimmed = raw.trim();
  if (trimmed === 'bold' || trimmed === 'bolder') return true;
  const n = parseInt(trimmed, 10);
  if (Number.isNaN(n)) return false;
  return n >= 600;
}

function applyMatrixToPoint(
  m: readonly [number, number, number, number, number, number],
  x: number,
  y: number,
): { x: number; y: number } {
  return {
    x: m[0] * x + m[2] * y + m[4],
    y: m[1] * x + m[3] * y + m[5],
  };
}

/** True when the matrix has a rotation/skew component that the
 *  editor's text-layer model can't carry. Pure scale + translate
 *  is fine — it maps to `fontSize` * scale + `x`/`y` translate. */
function hasNonRotationComponent(
  m: readonly [number, number, number, number, number, number],
): boolean {
  // Pure identity / scale / translate has m[1] = m[2] = 0.
  // Rotation has m[1] != 0 AND m[2] != 0 with m[1] = -m[2] (after
  // unit normalization). v1: reject any rotation; we don't carry
  // angle on text layers yet.
  const EPS = 1e-6;
  return Math.abs(m[1]) > EPS || Math.abs(m[2]) > EPS;
}
